import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Check, Sparkles, Zap, Crown, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useSubscription } from "@/hooks/useSubscription";

interface SubscriptionPlan {
  id: string;
  name: string;
  tier: string;
  price_cents_monthly: number;
  price_cents_yearly: number;
  credits_per_month: number;
}

interface SubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TIER_ICONS: Record<string, React.ReactNode> = {
  basic: <Zap className="h-5 w-5" />,
  pro: <Sparkles className="h-5 w-5" />,
  premium: <Crown className="h-5 w-5" />,
};

const TIER_COLORS: Record<string, string> = {
  basic: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  pro: "bg-purple-500/10 text-purple-300 border-purple-500/30",
  premium: "bg-amber-500/10 text-amber-500 border-amber-500/30",
};

const TIER_ORDER: Record<string, number> = {
  basic: 1,
  pro: 2,
  premium: 3,
};

export function SubscriptionModal({ open, onOpenChange }: SubscriptionModalProps) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [yearlyBilling, setYearlyBilling] = useState(false);
  const { t } = useTranslation();
  const { hasActiveSubscription, subscription, switchPlan, checkSubscription } = useSubscription();

  useEffect(() => {
    if (open) {
      fetchPlans();
      // Set billing toggle to match current subscription
      if (subscription?.billingPeriod === "yearly") {
        setYearlyBilling(true);
      }
    }
  }, [open, subscription?.billingPeriod]);

  const fetchPlans = async () => {
    const { data, error } = await supabase
      .from("subscription_plans")
      .select("*")
      .eq("is_active", true)
      .order("price_cents_monthly");

    if (error) {
      console.error("Error fetching plans:", error);
      return;
    }

    setPlans(data || []);
  };

  const handleSubscribe = async (planId: string) => {
    setLoading(true);
    setSelectedPlan(planId);

    try {
      const { data, error } = await supabase.functions.invoke("create-subscription", {
        body: {
          planId,
          billingPeriod: yearlyBilling ? "yearly" : "monthly",
        },
      });

      if (error) throw error;

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error("Error creating subscription:", error);
      toast.error(error.message || "Failed to start subscription");
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleSwitchPlan = async (planId: string) => {
    setLoading(true);
    setSelectedPlan(planId);

    try {
      const result = await switchPlan(planId, yearlyBilling ? "yearly" : "monthly");
      toast.success(result.message || "Plan switched successfully!");
      await checkSubscription();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error switching plan:", error);
      toast.error(error.message || "Failed to switch plan");
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  const getMonthlyEquivalent = (yearlyCents: number) => {
    return (yearlyCents / 12 / 100).toFixed(2);
  };

  const getSavings = (monthly: number, yearly: number) => {
    const yearlyMonthly = yearly / 12;
    const savings = ((monthly - yearlyMonthly) / monthly) * 100;
    return Math.round(savings);
  };

  const getPlanAction = (plan: SubscriptionPlan) => {
    if (!hasActiveSubscription || !subscription) {
      return { label: t("subscription.subscribe", "Subscribe"), action: "subscribe" };
    }

    if (subscription.planId === plan.id) {
      return { label: t("subscription.currentPlan", "Current Plan"), action: "current" };
    }

    const currentTierOrder = TIER_ORDER[subscription.planTier] || 0;
    const planTierOrder = TIER_ORDER[plan.tier] || 0;

    if (planTierOrder > currentTierOrder) {
      return { label: t("subscription.upgrade", "Upgrade"), action: "upgrade" };
    } else {
      return { label: t("subscription.downgrade", "Downgrade"), action: "downgrade" };
    }
  };

  const handlePlanClick = (plan: SubscriptionPlan) => {
    const { action } = getPlanAction(plan);
    
    if (action === "current") return;
    
    if (action === "subscribe") {
      handleSubscribe(plan.id);
    } else {
      handleSwitchPlan(plan.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {hasActiveSubscription 
              ? t("subscription.managePlan", "Manage Your Plan")
              : t("subscription.choosePlan", "Choose Your Plan")
            }
          </DialogTitle>
          <DialogDescription className="text-center">
            {hasActiveSubscription
              ? t("subscription.switchSubtitle", "Switch plans anytime. Your credits are kept and prorated charges apply.")
              : t("subscription.subtitle", "Subscribe to unlock AI video, image, music, and text generation")
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-3 my-4">
          <Label htmlFor="billing-toggle" className={!yearlyBilling ? "font-semibold" : "text-muted-foreground"}>
            {t("subscription.monthly", "Monthly")}
          </Label>
          <Switch
            id="billing-toggle"
            checked={yearlyBilling}
            onCheckedChange={setYearlyBilling}
          />
          <Label htmlFor="billing-toggle" className={yearlyBilling ? "font-semibold" : "text-muted-foreground"}>
            {t("subscription.yearly", "Yearly")}
            <Badge variant="secondary" className="ml-2 bg-green-500/10 text-green-500">
              {t("subscription.save20", "Save 20%")}
            </Badge>
          </Label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const price = yearlyBilling ? plan.price_cents_yearly : plan.price_cents_monthly;
            const isPopular = plan.tier === "pro";
            const { label, action } = getPlanAction(plan);
            const isCurrent = action === "current";

            return (
              <Card 
                key={plan.id} 
                className={`relative ${isPopular ? "border-primary shadow-lg" : ""} ${isCurrent ? "ring-2 ring-primary" : ""}`}
              >
                {isPopular && !isCurrent && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary">
                    {t("subscription.mostPopular", "Most Popular")}
                  </Badge>
                )}
                {isCurrent && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500">
                    {t("subscription.currentPlan", "Current Plan")}
                  </Badge>
                )}
                <CardHeader className="text-center pb-2">
                  <div className={`w-12 h-12 rounded-full mx-auto flex items-center justify-center ${TIER_COLORS[plan.tier]}`}>
                    {TIER_ICONS[plan.tier]}
                  </div>
                  <CardTitle className="text-xl mt-2">{plan.name}</CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-foreground">
                      ${yearlyBilling ? getMonthlyEquivalent(price) : formatPrice(price)}
                    </span>
                    <span className="text-muted-foreground">/mo</span>
                    {yearlyBilling && (
                      <div className="text-sm text-muted-foreground mt-1">
                        ${formatPrice(price)}/year
                      </div>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {plan.credits_per_month.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t("subscription.creditsPerMonth", "credits per month")}
                    </div>
                  </div>

                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-500" />
                      <span className="text-emerald-500 font-medium">{t("subscription.featureFreeImages", "Free AI Image Generation")}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      {t("subscription.feature1", "AI Video Generation")}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      {t("subscription.feature2", "AI Image Generation")}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      {t("subscription.feature3", "AI Music Generation")}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      {t("subscription.feature4", "AI Text Chat")}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      {t("subscription.feature5", "Credits roll over")}
                    </li>
                  </ul>

                  <Button 
                    className="w-full" 
                    variant={isCurrent ? "secondary" : isPopular ? "default" : "outline"}
                    onClick={() => handlePlanClick(plan)}
                    disabled={loading || isCurrent}
                  >
                    {loading && selectedPlan === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : action === "upgrade" ? (
                      <ArrowUp className="h-4 w-4 mr-2" />
                    ) : action === "downgrade" ? (
                      <ArrowDown className="h-4 w-4 mr-2" />
                    ) : null}
                    {label}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-4 space-y-1">
          <p className="text-xs text-muted-foreground">
            {hasActiveSubscription
              ? t("subscription.prorationNote", "Plan changes are prorated. Your existing credits are always kept.")
              : t("subscription.securePayment", "Secure payment powered by Stripe. Cancel anytime.")
            }
          </p>
          <p className="text-xs text-emerald-500 font-medium">
            {t("subscription.cancelAnytimeKeepCredits", "Cancel anytime - your credits are always kept")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}